/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react/require-default-props */
import * as time from '@helpers/time';
import EmpreintesCarboneAPIService from '@services/bytel-api/EmpreintesCarboneAPIService';
import { EmpreintesCarboneSearchFilters } from '@services/bytel-api/types';
import { EmpreintesCarboneEquipementsResponse } from '@services/interfaces/generated/obtenirEmpreintesCarboneEquipements';
import { AxiosError } from 'axios';
import React from 'react';
import { UseQueryResult } from 'react-query';
import { useIdToken } from '../auth-provider';
import { useOverrideQuery } from '../helpers';
import { useBytelBQuery } from '../bytel-bquery-provider';

const EmpreintesCarboneAPIContext = React.createContext<EmpreintesCarboneAPIService | null>(null);
const EmpreintesCarboneCacheKey = 'EmpreintesCarbone';

export type EmpreintesCarboneAPIProviderProps = {
  mockInstance?: EmpreintesCarboneAPIService;
};

/**
 * Provider pour l'API Bytel - Empreintes Carbone
 * Utilise l'instance BQuery Bytel partagée depuis BytelBQueryProvider
 */
export const EmpreintesCarboneAPIProvider: React.FC<EmpreintesCarboneAPIProviderProps> = ({
  children,
  mockInstance = undefined,
}) => {
  const bytelBQuery = useBytelBQuery();

  const instance = React.useMemo(() => {
    if (!!mockInstance) return mockInstance;

    return new EmpreintesCarboneAPIService(bytelBQuery);
  }, [mockInstance, bytelBQuery]);

  return <EmpreintesCarboneAPIContext.Provider value={instance}>{children}</EmpreintesCarboneAPIContext.Provider>;
};

/**
 * Hook pour accéder au service API Empreintes Carbone
 */
export const useEmpreintesCarboneAPI = (): EmpreintesCarboneAPIService => {
  const context = React.useContext(EmpreintesCarboneAPIContext);
  if (context === undefined || context === null) {
    throw new Error('The `useEmpreintesCarboneAPI` hook must be used within a EmpreintesCarboneAPIProvider');
  }
  return context;
};

/**
 * Hook pour récupérer les empreintes carbone des équipements
 * @param options - Filtres de recherche (siren, marque, dates, etc.)
 * @returns Les données des empreintes carbone
 */
export const useObtenirEmpreintesCarboneEquipements = (
  options?: EmpreintesCarboneSearchFilters,
): UseQueryResult<EmpreintesCarboneEquipementsResponse | undefined, AxiosError> => {
  const empreintesCarboneAPI = useEmpreintesCarboneAPI();
  const idToken = useIdToken(); // Pour invalider le cache quand le token change

  // Crée une clé de cache unique basée sur les filtres ET le token
  // Important : inclure le token pour invalider le cache quand l'utilisateur change
  const cacheKey = [EmpreintesCarboneCacheKey, options?.siren, options?.marque, options?.dateAchatMin, idToken];

  return useOverrideQuery(
    cacheKey,
    () => {
      return empreintesCarboneAPI.obtenirEmpreintesCarboneEquipements(options);
    },
    {
      cacheTime: time.fromMinutes(5), // Cache pendant 5 minutes
      staleTime: time.fromMinutes(2), // Considéré comme obsolète après 2 minutes
      enabled: !!idToken, // Ne lance la requête que si on a un token
    },
  );
};
